import React, { useState } from 'react';
import './LandingPage.css';

const LandingPage = ({ onGetStarted }) => {
    const [activeModal, setActiveModal] = useState(null);

    const openModal = (modalId) => {
        setActiveModal(modalId);
        document.body.style.overflow = 'hidden';
    };

    const closeModal = () => {
        setActiveModal(null);
        document.body.style.overflow = 'auto';
    };

    const handleGetStarted = () => {
        if (onGetStarted) {
            onGetStarted();
        }
    };

    return (
        <div className="landing-page">
            {/* Hero Section */}
            <section className="hero">
                <div className="hero-content">
                    <h1>🏡 Calculate Your True Real Estate Returns</h1>
                    <p>Accurate ROI with installments, taxes, and all costs - in minutes</p>
                    <button onClick={handleGetStarted} className="cta-button">
                        Start Calculating Now →
                    </button>
                </div>
            </section>

            {/* Video Section - Right Below Hero */}
            <section className="video-section-top">
                <div className="container">
                    <h2 className="video-heading">📊 Why You Need XIRR for Real Estate</h2>
                    <p className="video-subtitle">Watch this 5-minute explanation on why traditional ROI calculations mislead property investors</p>
                    <div className="video-container-top">
                        <video
                            controls
                            className="overview-video-top"
                        >
                            <source src="/overview-video.mp4" type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                        <p className="video-credit-top">Created with Google NotebookLM</p>
                    </div>
                </div>
            </section>

            {/* Key Benefits - Compact */}
            <section className="benefits-compact">
                <div className="container">
                    <h2>Why Use This Calculator?</h2>
                    <div className="benefits-grid-compact">
                        <div className="benefit-item">
                            <div className="benefit-icon">📊</div>
                            <h3>True XIRR/CAGR</h3>
                            <p>Accounts for multiple installments over time</p>
                        </div>
                        <div className="benefit-item">
                            <div className="benefit-icon">💰</div>
                            <h3>All Costs Included</h3>
                            <p>Registry, stamp duty, taxes - everything</p>
                        </div>
                        <div className="benefit-item">
                            <div className="benefit-icon">🎯</div>
                            <h3>Tax Impact</h3>
                            <p>See how capital gains affect your returns</p>
                        </div>
                        <div className="benefit-item">
                            <div className="benefit-icon">⚡</div>
                            <h3>Instant Results</h3>
                            <p>No Excel sheets, get answers in 5 minutes</p>
                        </div>
                        <div className="benefit-item">
                            <div className="benefit-icon">🔒</div>
                            <h3>100% Private</h3>
                            <p>All calculations in your browser</p>
                        </div>
                        <div className="benefit-item">
                            <div className="benefit-icon">📱</div>
                            <h3>Works Anywhere</h3>
                            <p>Mobile-friendly, use on any device</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works - Ultra Compact */}
            <section className="how-it-works-compact">
                <div className="container">
                    <h2>How It Works</h2>
                    <div className="steps-compact">
                        <div className="step-compact">
                            <span className="step-num">1</span>
                            <span>Add Purchase Details</span>
                        </div>
                        <div className="step-compact">
                            <span className="step-num">2</span>
                            <span>Add Sale Details</span>
                        </div>
                        <div className="step-compact">
                            <span className="step-num">3</span>
                            <span>Include All Expenses</span>
                        </div>
                        <div className="step-compact">
                            <span className="step-num">4</span>
                            <span>Get Instant Results</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="cta-section">
                <div className="container">
                    <h2>Ready to Know Your True Returns?</h2>
                    <button onClick={handleGetStarted} className="cta-button">
                        Launch Calculator
                    </button>
                </div>
            </section>

            {/* Compact Footer */}
            <footer className="footer-compact">
                <div className="container">
                    <div className="footer-links-compact">
                        <button onClick={() => openModal('terms')} className="footer-link">Terms</button>
                        <button onClick={() => openModal('privacy')} className="footer-link">Privacy</button>
                        <button onClick={() => openModal('disclaimer')} className="footer-link">Disclaimer</button>
                        <button onClick={() => openModal('contact')} className="footer-link">Contact</button>
                    </div>
                    <p className="footer-text">© 2026 Real Estate ROI Calculator | Made for Indian Property Investors</p>
                    <p className="footer-disclaimer">
                        ⚠️ For informational purposes only. Not financial/tax advice. Consult professionals before making decisions.
                    </p>
                </div>
            </footer>

            {/* Modals */}
            {activeModal && (
                <div className="modal" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <span className="close" onClick={closeModal}>&times;</span>
                        {activeModal === 'terms' && <TermsModal />}
                        {activeModal === 'privacy' && <PrivacyModal />}
                        {activeModal === 'disclaimer' && <DisclaimerModal />}
                        {activeModal === 'contact' && <ContactModal />}
                    </div>
                </div>
            )}
        </div>
    );
};

// Simplified Modal Components
const TermsModal = () => (
    <>
        <h2>Terms & Conditions</h2>
        <p>By using this calculator, you agree to:</p>
        <ul>
            <li>Use for personal, non-commercial purposes only</li>
            <li>Provide accurate information</li>
            <li>Not use for illegal purposes</li>
        </ul>
        <p><strong>Disclaimer:</strong> Service provided "as is" without warranties. Not financial/legal/tax advice.</p>
        <p><em>Last Updated: January 2026</em></p>
    </>
);

const PrivacyModal = () => (
    <>
        <h2>Privacy Policy</h2>
        <p><strong>Your Privacy is Protected:</strong></p>
        <ul>
            <li>All calculations performed in your browser</li>
            <li>No financial data sent to servers</li>
            <li>No data collection or storage</li>
            <li>May use cookies for analytics</li>
        </ul>
        <p><em>Last Updated: January 2026</em></p>
    </>
);

const DisclaimerModal = () => (
    <>
        <h2>Disclaimer</h2>
        <p><strong>Important:</strong> This calculator is for educational purposes only.</p>
        <ul>
            <li>Not financial, tax, or legal advice</li>
            <li>No guarantee of accuracy</li>
            <li>Tax laws change frequently</li>
            <li>Always consult certified professionals</li>
        </ul>
        <p><em>Last Updated: January 2026</em></p>
    </>
);

const ContactModal = () => (
    <>
        <h2>Contact Us</h2>
        <p><strong>Email:</strong> support@realestateroicalculator.com</p>
        <p><strong>Response Time:</strong> 24-48 hours</p>
        <p><strong>Note:</strong> We cannot provide personalized financial or tax advice. Please consult certified professionals.</p>
    </>
);

export default LandingPage;
